import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { buildDragpinAnswerRows } from './dragpinAnswer';

/**
 * useDragpinInteractive
 * - Memindahkan seluruh logic dragpin dari QuestionsForm supaya file utama tidak terlalu panjang.
 * - IMPORTANT: tidak mengubah logic existing (copy-paste + wrapping ke hook).
 */
export function useDragpinInteractive(params) {
  const {
    data,
    dispatch,
    setField,
    isInteractiveEnabled,
  } = params;

  // Flag khusus: saat fill_in_the_blanks + interactive + dragpin, sembunyikan input Label & Nilai
  const isDragpinInteractiveSentence = useMemo(() => {
    const isFillInTheBlanks = data.type === 'fill_in_the_blanks';
    const isInteractive = isInteractiveEnabled;
    const isDragpin = data.interactive_type === 'dragpin';
    return isFillInTheBlanks && isInteractive && isDragpin;
  }, [data.type, isInteractiveEnabled, data.interactive_type]);

  // ✅ Local state khusus dragpin untuk mapping pinIndex -> room (TIDAK masuk Redux)
  // Values: number (1..room_count) atau '' jika belum dipilih/di-clear
  const [dragpinPinsRoom, setDragpinPinsRoom] = useState([]);

  // Keep latest mapping in ref so effects can read without depending on array identity
  const dragpinPinsRoomRef = useRef([]);

  useEffect(() => {
    dragpinPinsRoomRef.current = Array.isArray(dragpinPinsRoom) ? dragpinPinsRoom : [];
  }, [dragpinPinsRoom]);

  // ✅ Guard supaya saat edit/update, mapping dragpin diambil dulu dari data existing
  // dan tidak langsung ke-overwrite oleh effect generator.
  const dragpinHydratedFromAnswerRef = useRef(false);
  const dragpinHydrationKeyRef = useRef('');
  const dragpinQuestionIdRef = useRef(data?.id);

  // Reset local mapping synchronously saat ganti soal supaya effect lain tidak baca mapping lama
  useLayoutEffect(() => {
    if (dragpinQuestionIdRef.current === data?.id) return;

    dragpinQuestionIdRef.current = data?.id;
    dragpinHydratedFromAnswerRef.current = false;
    dragpinHydrationKeyRef.current = '';
    setDragpinPinsRoom([]);
  }, [data?.id]);

  // Options jumlah room untuk dragpin (1 sampai 6)
  const dragPinsRoomOptions = useMemo(() => {
    return Array.from({ length: 6 }, (_, idx) => {
      const value = idx + 1;
      return { value, label: String(value) };
    });
  }, []);

  // Handle select jumlah room dragpin
  const handleSelectChangeDragPinsRooms = useCallback((selectedOption) => {
    if (selectedOption !== null) {
      const value = selectedOption.value;
      // Validasi range 1-6
      if (Number.isInteger(value) && value >= 1 && value <= 6) {
        dispatch(setField({ field: 'room_count', value }));
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Nilai Tidak Valid',
          text: 'Jumlah room harus antara 1 sampai 6.',
        });
      }
      return;
    }
    dispatch(setField({ field: 'room_count', value: '' }));
  }, [dispatch, setField]);

  // Options jumlah pins untuk dragpin (1 sampai 10)
  const dragPinsPinsOptions = useMemo(() => {
    return Array.from({ length: 10 }, (_, idx) => {
      const value = idx + 1;
      return { value, label: String(value) };
    });
  }, []);

  // Handle select jumlah pins dragpin
  const handleSelectChangeDragPinsPins = useCallback((selectedOption) => {
    if (selectedOption !== null) {
      const value = selectedOption.value;
      // Validasi range 1-10
      if (Number.isInteger(value) && value >= 1 && value <= 10) {
        dispatch(setField({ field: 'pin_count', value }));
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Nilai Tidak Valid',
          text: 'Jumlah pins harus antara 1 sampai 10.',
        });
      }
      return;
    }
    dispatch(setField({ field: 'pin_count', value: '' }));
  }, [dispatch, setField]);

  // Default names untuk Drag Pin (max 10 pins)
  const DRAGPIN_PIN_NAMES = useMemo(() => ([
    'Pin 1',
    'Pin 2',
    'Pin 3',
    'Pin 4',
    'Pin 5',
    'Pin 6',
    'Pin 7',
    'Pin 8',
    'Pin 9',
    'Pin 10',
  ]), []);

  // ✅ Ambil meta pins dari interactive_detail (disimpan di Redux)
  const dragpinPinsMeta = useMemo(() => {
    const meta = data?.interactive_detail?.dragpin_pins;
    return Array.isArray(meta) ? meta : [];
  }, [data?.interactive_detail?.dragpin_pins]);

  // ✅ Ref untuk selalu dapat interactive_detail terbaru di dalam callback (hindari stale closure)
  const interactiveDetailRef = useRef(data?.interactive_detail);
  interactiveDetailRef.current = data?.interactive_detail;

  // ✅ Helper set interactive_detail tanpa menimpa field lain
  const setDragpinPinsMeta = useCallback((next) => {
    const safeDetail = (interactiveDetailRef.current && typeof interactiveDetailRef.current === 'object')
      ? interactiveDetailRef.current
      : {};

    const nextMeta = typeof next === 'function' ? next(
      Array.isArray(safeDetail.dragpin_pins) ? safeDetail.dragpin_pins : []
    ) : next;

    dispatch(setField({
      field: 'interactive_detail',
      value: {
        ...safeDetail,
        dragpin_pins: nextMeta,
      },
    }));
  }, [dispatch, setField]);

  // Parse answer dragpin yang sudah tersimpan ke mapping local pinIndex -> room.
  // Format yang di-handle:
  // - "Room 1:null"
  // - "Room 1:Ika,Hafidh"
  // - "Room 1: Ika, Hafidh" (legacy yang pakai spasi)
  const parseDragpinPinsRoomFromAnswer = useCallback((answerArr, visibleNames, roomCount, pinsCount) => {
    const safeRoomCount = Number.isInteger(roomCount) && roomCount >= 1 && roomCount <= 6 ? roomCount : 1;
    const safePinsCount = Number.isInteger(pinsCount) && pinsCount >= 1 && pinsCount <= 10 ? pinsCount : 1;

    const result = Array.from({ length: safePinsCount }, () => '');

    // Baca dari ref supaya tidak stale saat navigasi
    const currentInteractiveDetail = interactiveDetailRef.current;

    // Build lookup untuk match case-insensitive
    // 1) dragpin_pins (name dari backend)
    // 2) pin_custom_names (kalau answer pakai custom name)
    // 3) visibleNames (fallback default)
    const nameToIndex = new Map();
    const currentDragpinPins = Array.isArray(currentInteractiveDetail?.dragpin_pins)
      ? currentInteractiveDetail.dragpin_pins
      : [];
    currentDragpinPins
      .filter((item) => item && typeof item === 'object')
      .forEach((pin, idx) => {
        const key = String(pin.name ?? '').trim().toLowerCase();
        if (key) nameToIndex.set(key, idx);
      });

    const pinCustomNames = Array.isArray(currentInteractiveDetail?.pin_custom_names)
      ? currentInteractiveDetail.pin_custom_names
      : [];
    pinCustomNames.forEach((name, idx) => {
      const key = String(name ?? '').trim().toLowerCase();
      if (key && !nameToIndex.has(key)) nameToIndex.set(key, idx);
    });

    (Array.isArray(visibleNames) ? visibleNames : []).forEach((name, idx) => {
      const key = String(name ?? '').trim().toLowerCase();
      if (key && !nameToIndex.has(key)) nameToIndex.set(key, idx);
    });

    const answers = Array.isArray(answerArr) ? answerArr : [];
    if (answers.length === 0) return result;

    answers.forEach((row) => {
      if (typeof row !== 'string') return;

      const match = row.match(/^\s*Room\s*(\d+)\s*:\s*(.*)\s*$/i);
      if (!match) return;

      const roomNumber = Number(match[1]);
      if (!Number.isInteger(roomNumber) || roomNumber < 1 || roomNumber > safeRoomCount) return;

      const rawNames = (match[2] ?? '').trim();
      if (!rawNames || rawNames.toLowerCase() === 'null') return;

      // Split dengan tolerate spasi setelah koma.
      const names = rawNames
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean);

      names.forEach((pinName) => {
        const key = String(pinName).trim().toLowerCase();
        const pinIndex = nameToIndex.get(key);
        if (pinIndex === undefined) return;
        result[pinIndex] = roomNumber;
      });
    });

    console.log('🔍 parseDragpinPinsRoomFromAnswer:', {
      currentDragpinPins,
      pinCustomNames,
      nameToIndex: Object.fromEntries(nameToIndex),
      answerArr,
      result
    });

    return result;
    // Semua baca dari interactiveDetailRef.current (ref, selalu fresh) — deps cukup []
  }, []);

  // Re-hydrate mapping ketika dragpin meta selesai di-restore (edit mode)
  useEffect(() => {
    if (!isDragpinInteractiveSentence) return;
    if (dragpinHydratedFromAnswerRef.current) return;

    const hasMeta = Array.isArray(dragpinPinsMeta) && dragpinPinsMeta.length > 0;
    const answerArr = Array.isArray(data?.answer) ? data.answer : [];
    const hasDragpinAnswerShape = answerArr.some(
      (row) => typeof row === 'string' && /\bRoom\s*\d+\s*:/i.test(row)
    );

    // Hanya reset flag kalau data answer sudah siap tapi mapping belum pernah di-hydrate
    if (hasMeta && hasDragpinAnswerShape) {
      dragpinHydrationKeyRef.current = '';
    }
  }, [isDragpinInteractiveSentence, dragpinPinsMeta, data?.answer]);

  // Ambil pins yang ditampilkan berdasarkan pin_count (1..5)
  const dragPinsVisibleNames = useMemo(() => {
    const pins = Number(data?.pin_count);
    if (!Number.isInteger(pins) || pins < 1) return [];
    return DRAGPIN_PIN_NAMES.slice(0, Math.min(pins, DRAGPIN_PIN_NAMES.length));
  }, [data?.pin_count, DRAGPIN_PIN_NAMES]);

  // ✅ Normalisasi meta pins supaya selalu sinkron dengan jumlah pin & nama (custom atau default)
  // PENTING: effect ini hanya sinkronisasi nama pin — tidak boleh overwrite label.
  // Tidak perlu dragpinPinsMeta di deps supaya tidak terpicu saat user ketik deskripsi.
  useEffect(() => {
    if (!isDragpinInteractiveSentence) {
      // Clear dragpin meta saat keluar dari mode dragpin (skip saat edit/hydrate masih loading)
      if (!data?.id) {
        setDragpinPinsMeta((prev) => (Array.isArray(prev) && prev.length > 0 ? [] : prev));
      }
      return;
    }

    const visibleNames = dragPinsVisibleNames;

    // Ambil custom pin names kalau diisi
    const pinCustomNames = Array.isArray(data?.interactive_detail?.pin_custom_names)
      ? data.interactive_detail.pin_custom_names
      : [];

    // Baca current meta langsung dari ref supaya tidak stale
    const currentMeta = Array.isArray(interactiveDetailRef.current?.dragpin_pins)
      ? interactiveDetailRef.current.dragpin_pins
      : [];

    // Build lookup label by INDEX dari current meta
    const labelByIndex = new Map(
      currentMeta
        .filter((item) => item && typeof item === 'object')
        .map((item, idx) => [idx, item.label ?? ''])
    );

    // Update name berdasarkan custom name kalau ada, fallback ke default
    const nextMeta = visibleNames.map((name, idx) => ({
      name: String(pinCustomNames[idx] ?? '').trim().length ? String(pinCustomNames[idx]).trim() : name,
      label: labelByIndex.get(idx) ?? '',
    }));

    const isSameLength = nextMeta.length === currentMeta.length;
    const isSameValues = isSameLength && nextMeta.every((row, idx) => (
      row.name === currentMeta[idx]?.name && row.label === currentMeta[idx]?.label
    ));

    if (!isSameValues) {
      console.log('🔄 Normalize dragpinPinsMeta:', {
        visibleNames,
        pinCustomNames,
        currentMeta,
        nextMeta,
        labelByIndex: Object.fromEntries(labelByIndex)
      });
      setDragpinPinsMeta(nextMeta);
    }
  }, [
    isDragpinInteractiveSentence,
    dragPinsVisibleNames,
    data?.id,
    data?.interactive_detail?.pin_custom_names,
    // SENGAJA tidak include dragpinPinsMeta — effect ini hanya sync nama,
    // bukan listener perubahan label. Kalau dimasukkan, setiap ketik deskripsi
    // akan trigger normalize dan bisa overwrite label pin lain.
    setDragpinPinsMeta,
  ]);

  // ✅ Handle change label per pin (wajib diisi untuk dragpin)
  const handleChangeDragpinPinLabel = useCallback((pinIndex, value) => {
    setDragpinPinsMeta((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const next = [...current];
      // Preserve existing name (custom atau default) — jangan overwrite dengan default "Pin 1"
      const existingName = current[pinIndex]?.name;
      const fallbackName = dragPinsVisibleNames[pinIndex] || '';
      const safeName = existingName || fallbackName;

      next[pinIndex] = {
        name: safeName,
        label: value,
      };

      return next;
    });
  }, [dragPinsVisibleNames, setDragpinPinsMeta]);

  // Options room untuk picker pins: 1..room_count (mengikuti jumlah room yang dipilih)
  const dragPinsRoomOptionsBySelectedRoomCount = useMemo(() => {
    const room = Number(data?.room_count);
    if (!Number.isInteger(room) || room < 1 || room > 6) return [];
    return Array.from({ length: room }, (_, idx) => {
      const value = idx + 1;
      return { value, label: String(value) };
    });
  }, [data?.room_count]);

  // Build "Nilai" (answer) khusus dragpin dari mapping pinsRoom
  // Format: "Room X:null" atau "Room X:Lilis,Nurul" (nama dalam room diurutkan alfabet, sama seperti App)
  const buildDragpinAnswerByRooms = useCallback((pinsRoomOverride) => {
    const pinCustomNames = Array.isArray(data?.interactive_detail?.pin_custom_names)
      ? data.interactive_detail.pin_custom_names
      : [];
    const visibleNames = dragPinsVisibleNames.map((name, idx) => {
      const customName = String(pinCustomNames[idx] ?? '').trim();
      return customName.length ? customName : name;
    });

    return buildDragpinAnswerRows({
      roomCount: data?.room_count,
      visibleNames,
      pinsRoom: Array.isArray(pinsRoomOverride) ? pinsRoomOverride : [],
    });
  }, [data?.room_count, data?.interactive_detail?.pin_custom_names, dragPinsVisibleNames]);

  // Update mapping room per pin (pins_room: number[])
  const handleSelectChangeDragPinPinRoom = useCallback((pinIndex, selectedOption) => {
    setDragpinPinsRoom((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const next = [...current];
      // Jika di-clear, set jadi '' (unassigned)
      next[pinIndex] = selectedOption !== null ? selectedOption.value : '';

      // Sync answer segera supaya tidak tersimpan null semua
      const generated = buildDragpinAnswerByRooms(next);
      if (generated.length) {
        dispatch(setField({ field: 'answer', value: generated }));
      }

      return next;
    });
  }, [buildDragpinAnswerByRooms, dispatch, setField]);

  // ✅ Normalize local mapping saat dragpin aktif (mengikuti pin_count & room_count)
  useEffect(() => {
    if (!isDragpinInteractiveSentence) {
      // Saat keluar dari mode dragpin, local state boleh direset biar tidak nyangkut
      setDragpinPinsRoom((prev) => (Array.isArray(prev) && prev.length > 0 ? [] : prev));

      // Reset hydration flag supaya saat masuk dragpin lagi (atau edit item lain) bisa hydrate ulang.
      dragpinHydratedFromAnswerRef.current = false;
      dragpinHydrationKeyRef.current = '';
      return;
    }

    const roomRaw = Number(data?.room_count);
    const pinsRaw = Number(data?.pin_count);

    const isRoomCountValid = Number.isInteger(roomRaw) && roomRaw >= 1 && roomRaw <= 6;
    const isPinsCountValid = Number.isInteger(pinsRaw) && pinsRaw >= 1 && pinsRaw <= 10;

    const safeRoomCount = isRoomCountValid ? roomRaw : 1;
    const safePinsCount = isPinsCountValid ? pinsRaw : 1;

    const visibleNames = DRAGPIN_PIN_NAMES.slice(0, Math.min(safePinsCount, DRAGPIN_PIN_NAMES.length));
    const answerArr = Array.isArray(data?.answer) ? data.answer : [];
    const hasDragpinAnswerShape = answerArr.some((row) => typeof row === 'string' && /\bRoom\s*\d+\s*:/i.test(row));

    // Untuk dragpin yang tersimpan, answer length normalnya === room_count.
    // Ini jadi guard supaya tidak hydrate terlalu cepat saat edit (sebelum room_count/pin_count kebaca).
    const isAnswerConsistentWithRoomCount = answerArr.length > 0 && answerArr.length === safeRoomCount;

    // Guard tambahan: kalau id di Redux belum sinkron dengan hydrationKey, skip dulu.
    // Di React 17, dispatch tidak di-batch → id di-dispatch terakhir di handleEditQuestion.
    // Ini mencegah hydrate di state campuran (room_count Q3, answer Q2, id masih Q2).
    const currentId = data?.id;

    // Helper: cek apakah mapping lokal sudah punya value valid (>=1)
    const currentPinsRoom = Array.isArray(dragpinPinsRoomRef.current) ? dragpinPinsRoomRef.current : [];

    // ✅ Saat edit/update: hydrate local mapping dari jawaban existing (kalau ada)
    // Hindari infinite loop: hanya setState kalau signature (id/room/pin/answer) berubah.
    // PENTING: sertakan data.id di hydrationKey supaya hydrate tidak jalan di state campuran
    // (saat room_count sudah Q3 tapi answer masih Q2 karena dispatch async tidak di-batch di React 17).
    if (hasDragpinAnswerShape && isRoomCountValid && isPinsCountValid && isAnswerConsistentWithRoomCount) {
      const normalizeForKey = (value) => (typeof value === 'string' ? value.replace(/\s+/g, '').toLowerCase() : String(value));
      const hydrationKey = `${currentId}|${safeRoomCount}|${safePinsCount}|${answerArr.map(normalizeForKey).join('~')}`;

      // Guard: kalau hydrationKey sudah include currentId tapi id belum final (masih soal lama),
      // key tidak akan match → hydration otomatis skip.
      const shouldHydrate =
        hydrationKey !== dragpinHydrationKeyRef.current ||
        !dragpinHydratedFromAnswerRef.current;

      if (shouldHydrate) {
        const parsed = parseDragpinPinsRoomFromAnswer(answerArr, visibleNames, safeRoomCount, safePinsCount);

        // Set hanya kalau berbeda, supaya tidak memicu render loop.
        const isSame =
          currentPinsRoom.length === parsed.length &&
          currentPinsRoom.every((v, i) => v === parsed[i]);

        dragpinHydrationKeyRef.current = hydrationKey;
        dragpinHydratedFromAnswerRef.current = true;

        console.log('💧 Hydrate dragpinPinsRoom from answer:', {
          answerArr,
          visibleNames,
          safeRoomCount,
          safePinsCount,
          parsed,
          isSame
        });

        if (!isSame) {
          setDragpinPinsRoom(parsed);
        }
        return;
      }

      // Sudah hydrated untuk soal ini — jangan clamp ulang supaya select room stabil
      if (dragpinHydratedFromAnswerRef.current) return;
    }

    // ✅ Kalau tidak ada data existing untuk di-hydrate (create mode), langsung set hydrated = true
    // supaya effect generator answer bisa jalan.
    if (!hasDragpinAnswerShape && !dragpinHydratedFromAnswerRef.current && isRoomCountValid && isPinsCountValid) {
      dragpinHydratedFromAnswerRef.current = true;
    }

    // Clamp & resize local array sesuai pin_count, dan validasi room range
    setDragpinPinsRoom((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const normalized = Array.from({ length: safePinsCount }, (_, idx) => {
        const rawValue = Number(current[idx]);
        if (Number.isInteger(rawValue) && rawValue >= 1 && rawValue <= safeRoomCount) return rawValue;
        // Default: kosong (unassigned) supaya tidak auto-mengubah jawaban saat edit/update.
        return '';
      });

      const isSameLength = current.length === normalized.length;
      const isSameValues = isSameLength && current.every((v, i) => v === normalized[i]);
      return isSameValues ? current : normalized;
    });
  }, [
    isDragpinInteractiveSentence,
    data?.id,
    data?.room_count,
    data?.pin_count,
    data?.answer,
    DRAGPIN_PIN_NAMES,
    parseDragpinPinsRoomFromAnswer,
    // NOTE: jangan masukkan dragpinPinsRoom ke deps (bisa memicu Maximum update depth).
  ]);

  // Saat dragpin aktif, sinkronkan data.answer dengan hasil generate (tetap ke Redux)
  useEffect(() => {
    if (!isDragpinInteractiveSentence) return;

    const current = Array.isArray(data?.answer) ? data.answer : [];
    const currentHasNames = current.some((row) => {
      if (typeof row !== 'string') return false;
      const match = row.match(/^\s*Room\s*\d+\s*:\s*(.*)\s*$/i);
      if (!match) return false;
      const right = (match[1] ?? '').trim();
      return right.length > 0 && right.toLowerCase() !== 'null';
    });

    // ✅ Kalau answer kosong atau cuma [''] (create mode), langsung generate tanpa tunggu hydrated.
    const isAnswerEmpty = current.length === 0 || (current.length === 1 && current[0] === '');

    if (!isAnswerEmpty) {
      // Edit mode: tunggu sampai mapping lokal selesai di-hydrate supaya tidak meng-overwrite answer lama.
      if (!dragpinHydratedFromAnswerRef.current) return;

      // Jangan overwrite answer existing yang sudah punya nama pins kalau mapping belum ada assignment.
      const pinsRoom = Array.isArray(dragpinPinsRoomRef.current) ? dragpinPinsRoomRef.current : [];
      const hasAnyAssigned = pinsRoom.some((v) => {
        const n = Number(v);
        return Number.isInteger(n) && n >= 1;
      });

      if (currentHasNames && !hasAnyAssigned) return;
    }

    const generated = buildDragpinAnswerByRooms(
      Array.isArray(dragpinPinsRoomRef.current) ? dragpinPinsRoomRef.current : []
    );
    if (!generated.length) return;

    // Compare secara whitespace-insensitive supaya tidak dispatch hanya gara-gara beda spasi.
    const normalizeRow = (value) => (typeof value === 'string' ? value.replace(/\s+/g, '').toLowerCase() : value);
    const isSameLength = current.length === generated.length;
    const isSameValues = isSameLength && current.every((v, i) => normalizeRow(v) === normalizeRow(generated[i]));

    // Hindari dispatch loop jika nilainya sama
    if (!isSameValues) {
      dispatch(setField({ field: 'answer', value: generated }));
    }
  }, [isDragpinInteractiveSentence, buildDragpinAnswerByRooms, data?.answer, data?.id, dispatch, setField]);

  return {
    isDragpinInteractiveSentence,
    dragpinPinsRoom,
    dragPinsRoomOptions,
    handleSelectChangeDragPinsRooms,
    dragPinsPinsOptions,
    handleSelectChangeDragPinsPins,
    dragPinsVisibleNames,
    dragPinsRoomOptionsBySelectedRoomCount,
    handleSelectChangeDragPinPinRoom,
    dragpinPinsMeta,
    handleChangeDragpinPinLabel,
  };
}
